<template>
	<div
		v-if="!(hideOnSinglePage && pageCount === 1)"
		class="pagination"
	>
		<helio-button
			class="pagination__prev"
			:disabled="currentPage === 1 || disabled"
			icon="chevron-left"
			icon-weight="light"
			:link="pageUrl(currentPage - 1)"
			@click="pageClick(currentPage - 1)"
		/>

		<template v-if="simple">
			<helio-dropdown
				:options="optionsArray"
				:disabled="disabled"
				:initial-selected="currentOption"
				:close-on-select="true"
				@changed="onDropdownChange"
			/>

			<span class="pagination__count-label">
				{{
					$tc('pagination.total-count',
						pageCount,
						{ count: pageCount })
				}}
			</span>
		</template>

		<template v-else>
			<template v-for="(option, i) in optionsArray">
				<helio-link
					:key="`prev-ellipsis-${i}`"
					:link="pageUrl(prevSkip)"
					@click="pageClick(prevSkip)"
				>
					<helio-icon
						v-if="option.value === minOption && option.value !== 1"
						:class="[
							'pagination__icon',
							{
								'pagination__icon--disabled': disabled,
							},
						]"
						:token="leftSkipIcon"
						weight="light"
						@mouseover.native="onMouse('over', 'left')"
						@mouseleave.native="onMouse('leave', 'left')"
					/>
				</helio-link>

				<helio-button
					v-if="option.value === 1
						|| option.value === pageCount
						|| (option.value >= minOption && option.value <= maxOption)"
					:key="i"
					class="pagination__page"
					:disabled="disabled"
					:link="pageUrl(option.value)"
					:text="option.label"
					:color="option.value === currentPage
						? 'primary'
						: 'normal'"
					@click="pageClick(option.value)"
				/>

				<helio-link
					:key="`next-ellipsis-${i}`"
					:link="pageUrl(nextSkip)"
					@click="pageClick(nextSkip)"
				>
					<helio-icon
						v-if="option.value === maxOption && option.value !== pageCount"
						:class="[
							'pagination__icon',
							{
								'pagination__icon--disabled': disabled,
							},
						]"
						:token="rightSkipIcon"
						weight="light"
						@mouseover.native="onMouse('over', 'right')"
						@mouseleave.native="onMouse('leave', 'right')"
					/>
				</helio-link>
			</template>
		</template>

		<helio-button
			class="pagination__next"
			:disabled="currentPage === pageCount || disabled"
			icon="chevron-right"
			icon-weight="light"
			:link="pageUrl(currentPage + 1)"
			@click="pageClick(currentPage + 1)"
		/>
	</div>
</template>

<script>
import Button from '../button/Button.vue';
import Dropdown from '../dropdown/Dropdown.vue';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Pagination',

	components: {
		'helio-button': Button,
		'helio-dropdown': Dropdown,
		'helio-icon': Icon,
	},

	props: {
		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Allows to deactivate pagination',
		},

		hideOnSinglePage: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets wether to hide pagination if there is only one page worth of content',
		},

		initialCurrent: {
			type: Number,
			required: false,
			default: 1,
			description: 'Sets the default page number',
		},

		linkMethod: {
			type: Function,
			required: false,
			default: null,
			description: 'Sets a custom function to generate helio-link instead of div that have an on-click event. Function recieves the page the number of the clicked page as a param',
		},

		pageSize: {
			type: Number,
			required: false,
			default: 10,
			description: 'Sets the number of elements per page',
		},

		simple: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets wether to use the simple display mode',
		},

		total: {
			type: Number,
			required: false,
			default: 0,
			description: 'Sets the total amount of elements available',
		},
	},

	data() {
		return {
			currentPage: 0,
			currentOption: {},
			dropdownInitialized: false,
			rightSkipIcon: 'ellipsis-h',
			leftSkipIcon: 'ellipsis-h',
		};
	},

	computed: {
		optionsArray() {
			return Array.from(
				{ length: this.pageCount },
				(_, i) => ({
					label: (i + 1).toString(),
					value: i + 1,
				}),
			);
		},

		pageCount() {
			return Math.ceil(this.total / this.pageSize);
		},

		minOption() {
			if (this.pageCount <= 7) {
				return 1;
			}

			if (this.currentPage <= 4) {
				return 1;
			}

			if (this.currentPage >= this.pageCount - 2) {
				return this.pageCount - 4;
			}

			return this.currentPage - 2;
		},

		maxOption() {
			if (this.pageCount <= 7) {
				return this.pageCount;
			}

			if (this.currentPage <= 3) {
				return 5;
			}

			if (this.currentPage >= this.pageCount - 3) {
				return this.pageCount;
			}

			return this.currentPage + 2;
		},

		prevSkip() {
			return Math.min(Math.max(1, this.currentPage - 5), this.pageCount);
		},

		nextSkip() {
			return Math.min(Math.max(1, this.currentPage + 5), this.pageCount);
		},
	},

	watch: {
		currentPage(newVal, oldVal) {
			if (oldVal !== 0) {
				this.$emit('changed', newVal);
			}
		},

		initialCurrent(newVal) {
			this.pageClick(newVal);
		},
	},

	mounted() {
		this.currentPage = this.initialCurrent;
		this.currentOption = this.optionsArray[this.initialCurrent - 1];

		if (this.simple && this.linkMethod) {
			console.warn('Cannot use linkMethod in simple mode');
		}
	},

	methods: {
		onDropdownChange(newVal) {
			if (!this.dropdownInitialized) {
				this.dropdownInitialized = true;
			} else if (newVal && newVal.length) {
				this.currentPage = newVal[0].value;
			}
		},

		pageClick(page) {
			if (!this.disabled) {
				this.currentPage = Math.min(Math.max(1, page), this.pageCount);
				this.currentOption = this.optionsArray[page - 1];
			}
		},

		pageUrl(pageNumber) {
			if (this.linkMethod) {
				return this.linkMethod(pageNumber);
			}

			return null;
		},

		onMouse(event, direction) {
			// Sets the values of rightSkipIcon and leftSkipIcon
			if (!this.disabled) {
				if (event === 'over') {
					this[`${direction}SkipIcon`] = `angle-double-${direction}`;
				} else {
					this[`${direction}SkipIcon`] = 'ellipsis-h';
				}
			}
		},
	},
};
</script>

<style lang="scss" src="./pagination.scss" />
